import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { pickText, useCoarsePointer, usePanoramaProgress, useTourState } from "@heroncendre/player";
import { MARGE_COIN } from "./SceneNav.js";
import { construireEspaces, espaceDe } from "./espaces.js";
import { etatNavigation, hauteurBas, useEtatNavigation } from "./etatNavigation.js";
import { ENTREE_MS, SORTIE_MS, demarrer, interrompre, phaseA, prochainChangement, type Sequence } from "./sequenceTitre.js";
import { COULEURS, POLICE_TITRE_SPLASH, POLICE_UI_SPLASH } from "./splashStyles.js";
import { useReducedMotion } from "./useReducedMotion.js";

/**
 * ============================================================================
 *  SceneTitle.tsx — Le titre d'arrivée sur une scène, « signature »
 * ----------------------------------------------------------------------------
 *  Référence : `TitreScene.dc.html` (variante signature) du handoff
 *  `design_handoff_titre_scene`. Comme un titre de documentaire, centré
 *  en bas : l'espace, puis le nom de la scène (aucun chiffre : ni rang ni total) ; sous le nom, un filet sauge qui se vide.
 *
 *  ── Quand ──────────────────────────────────────────────────────────────────
 *
 *  Quand la scène est PRÊTE — sa texture affichée (`usePanoramaProgress`) —, pas
 *  au clic : le titre ne doit pas se poser sur du noir. Une fois par arrivée :
 *  changer d'ambiance (même scène) ne le relance pas. Cinq secondes en tout ;
 *  le minutage est dans `sequenceTitre.ts`. Après ces cinq secondes le titre ne
 *  disparaît pas : il rapetisse et RESTE, centré en bas, jusqu'à la scène
 *  suivante (nom seul, sans espace ni filet). Durée de l'introduction FIXE : tourner la vue, la
 *  molette ou le clavier ne l'écourtent pas — on regarde autour de soi pendant
 *  qu'il se lit. Une nouvelle scène le relance.
 *
 *  Pas de titre quand l'indicateur de rotation occupe l'écran (première scène
 *  de la visite libre), ni sur téléphone quand la feuille de navigation est
 *  ouverte — l'ouvrir pendant le titre l'efface.
 *
 *  ── Où ─────────────────────────────────────────────────────────────────────
 *
 *  Ordinateur : la navigation s'efface pendant le titre (`etatNavigation.titre`)
 *  et revient, à sa fin, dans l'état exact d'avant (`finDuTitre`). Le titre se
 *  pose au-dessus du contrôleur d'ambiances s'il est là.
 *  Téléphone : la barre de navigation reste ; le titre se pose au-dessus de
 *  tout ce qui occupe le bas (barre, ambiances), à la hauteur MESURÉE
 *  (`useObstacleBas`), zone sûre comprise.
 *
 *  Le bloc visuel est muet (`aria-hidden`) ; une région `aria-live` dit
 *  « Espace : Nom de la scène ». Aucun texte nouveau : tout vient du document.
 * ==========================================================================*/

/** Entre le titre et ce qu'il surplombe. */
const ECART = 16;
/**
 * Sur ordinateur, le titre est 30 % plus grand que le handoff (40 / 11 / 54 px) :
 * à 54 px, il paraissait timide sur un grand écran. Tout le bloc suit, pour garder
 * ses proportions.
 */
const ECHELLE_ORDINATEUR = 1.3;

export function SceneTitle() {
  const tour = useTourState((s) => s.tour);
  const language = useTourState((s) => s.context.language);
  const sceneId = useTourState((s) => s.currentSceneId);
  const progression = usePanoramaProgress();
  const mobile = useCoarsePointer();
  const reduit = useReducedMotion();
  const ouvert = useEtatNavigation((e) => e.ouvert);
  const bas = useEtatNavigation((e) => e.bas);
  const espaces = useMemo(() => construireEspaces(tour), [tour]);

  const [{ sequence, maintenant }, setEtat] = useState<{ sequence: Sequence | null; maintenant: number }>(() => ({
    sequence: null,
    maintenant: 0,
  }));
  const appliquer = (transformer: (sequence: Sequence | null, instant: number) => Sequence | null) => {
    const instant = performance.now();
    setEtat((avant) => ({ sequence: transformer(avant.sequence, instant), maintenant: instant }));
  };
  const phase = phaseA(sequence, maintenant);
  // L'introduction (navigation effacée sur ordinateur) ; `compact` n'efface rien.
  const intro = phase === "attente" || phase === "affiche" || phase === "sortie";

  // Une arrivée = un titre : la scène est oubliée dès qu'elle change.
  const annoncee = useRef<string | null>(null);
  useEffect(() => {
    annoncee.current = null;
  }, [sceneId]);

  const prete =
    progression.status === "shown" && progression.sourceKey?.startsWith(`${sceneId}|`) === true;
  useEffect(() => {
    if (!prete || annoncee.current === sceneId) return;
    annoncee.current = sceneId;
    const nav = etatNavigation.lire();
    if (nav.indicateur || (mobile && nav.ouvert)) {
      etatNavigation.oublier();
      appliquer(() => null);
      return;
    }
    appliquer((_, instant) => demarrer(sceneId, instant));
  }, [prete, sceneId, mobile]);

  // Un seul minuteur : jusqu'au prochain changement de phase.
  useEffect(() => {
    const prochain = prochainChangement(sequence, maintenant);
    if (prochain === null) return;
    const minuteur = window.setTimeout(() => appliquer((s) => s), prochain - maintenant);
    return () => window.clearTimeout(minuteur);
  }, [sequence, maintenant]);

  // Le titre se signale ; à sa fin, la navigation retrouve son état d'avant.
  const etaitActif = useRef(false);
  useEffect(() => {
    if (intro) etatNavigation.setTitre(true);
    else if (etaitActif.current) etatNavigation.finDuTitre(mobile);
    etaitActif.current = intro;
  }, [intro, mobile]);
  useEffect(() => () => etatNavigation.finDuTitre(true), []);

  // Téléphone : ouvrir la feuille de navigation efface le titre.
  useEffect(() => {
    if (mobile && ouvert) appliquer(interrompre);
  }, [mobile, ouvert]);

  const scene = sequence ? tour.scenes.find((candidate) => candidate.id === sequence.sceneId) : undefined;
  const espace = sequence ? espaceDe(espaces, sequence.sceneId) : undefined;
  const nomScene = scene ? (pickText(scene.title, language) ?? "") : "";
  const nomEspace = espace ? (pickText(espace.titre, language) ?? "") : "";
  const visible = phase === "affiche";
  const compact = phase === "compact";
  // Réduit, le titre appartient à la scène courante ; d'une scène à l'autre il s'efface le temps du chargement.
  const present = sequence !== null && phase !== "masque" && (!compact || sequence.sceneId === sceneId);
  // Téléphone : la feuille de navigation recouvre le titre réduit.
  const cache = compact && mobile && ouvert;
  const annonce = phase === "affiche" || phase === "sortie" ? (nomEspace ? `${nomEspace} : ${nomScene}` : nomScene) : "";

  const hauteurAmbiances = bas.ambiances ?? 0;
  // Réduit dès que la sortie commence : le nom rapetisse pendant que le reste s'efface.
  const reduite = phase === "sortie" || compact;
  const bottom = mobile
    ? hauteurBas(bas) + ECART
    : hauteurAmbiances > 0
      ? Math.max(104, hauteurAmbiances + ECART)
      : MARGE_COIN; // même bas que le panneau de navigation

  // Entrée : glissement depuis la gauche. La sortie est un fondu seul : le titre n'est pas
  // déplacé ni redimensionné, il s'efface pendant que la forme réduite apparaît à sa place.
  const decalage = reduit || phase === "sortie" || visible ? "none" : "translateX(-18px)";
  const transition = (retard: number) =>
    phase === "sortie"
      ? "opacity .9s ease"
      : `opacity 1s ease ${retard}s, transform 1s cubic-bezier(.2,.7,.2,1) ${retard}s`;
  const position: CSSProperties = mobile
    ? {
        left: 22,
        right: 22,
        // La barre mesurée inclut déjà la zone sûre ; sans elle, on l'ajoute.
        bottom: bas.navigation ? bottom : `calc(${bottom}px + env(safe-area-inset-bottom))`,
      }
    : { left: 56, right: 56, bottom };

  return (
    <>
      <div aria-live="polite" style={horsEcran}>
        {annonce}
      </div>
      {present && scene && (
        <>
          <div
            aria-hidden
            style={{
              ...voile,
              opacity: cache ? 0 : visible ? 1 : compact ? 0.45 : phase === "sortie" ? 0.45 : 0,
              transition: `opacity ${phase === "sortie" ? 0.9 : 1}s ease`,
            }}
          />
          <div
            aria-hidden
            style={{ ...bloc, ...position, gap: mobile ? 10 : 14, opacity: cache ? 0 : 1, transition: "opacity .4s ease" }}
          >
            <div style={{ ...ligneHaut, opacity: visible ? 1 : 0, transform: decalage, transition: transition(0) }}>
              <span style={{ ...surtitre, fontSize: mobile ? 11 : 11 * ECHELLE_ORDINATEUR }}>{nomEspace}</span>
            </div>
            <h2
              style={{
                ...nom,
                fontSize: mobile ? 34 : 54 * ECHELLE_ORDINATEUR,
                maxWidth: mobile ? 720 : 720 * ECHELLE_ORDINATEUR,
                opacity: visible ? 1 : 0,
                transform: decalage,
                transition: transition(0.15),
              }}
            >
              {nomScene}
            </h2>
            {/* Le filet sauge, à l'horizontale sous le nom : il se vide de l'entrée à la sortie. */}
            <div style={{ ...filet, opacity: visible ? 1 : 0 }}>
              <div
                style={{
                  ...remplissage,
                  width: visible || phase === "sortie" ? "0%" : "100%",
                  transition: phase === "attente" ? "none" : `width ${SORTIE_MS - ENTREE_MS}ms linear`,
                }}
              />
            </div>
          </div>
          {/* La forme réduite : un autre bloc, à sa place et à sa taille définitives. */}
          <div
            aria-hidden
            style={{
              ...bloc,
              ...position,
              opacity: cache || !reduite ? 0 : 1,
              transition: cache ? "opacity .4s ease" : "opacity .9s ease",
            }}
          >
            <h2 style={{ ...nom, fontSize: mobile ? 26 : 34, maxWidth: mobile ? 720 : 720 * ECHELLE_ORDINATEUR }}>{nomScene}</h2>
          </div>
        </>
      )}
    </>
  );
}

/* --- styles (valeurs du handoff) ------------------------------------------ */

const horsEcran: CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  whiteSpace: "nowrap",
};

/** Le dégradé bas, pour le contraste AA du texte sur n'importe quel ciel ou plancher. */
const voile: CSSProperties = {
  position: "absolute",
  left: 0,
  right: 0,
  bottom: 0,
  height: "55%",
  background: "linear-gradient(0deg, rgba(11,19,16,.78), rgba(11,19,16,0))",
  pointerEvents: "none",
};

const bloc: CSSProperties = {
  position: "absolute",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  pointerEvents: "none",
  color: COULEURS.creme,
  fontFamily: POLICE_UI_SPLASH,
};

const filet: CSSProperties = {
  position: "relative",
  flex: "none",
  width: 96,
  height: 2,
  overflow: "hidden",
  background: "rgba(246,238,227,.2)",
  transition: "opacity .6s ease",
};

/** Le remplissage sauge, accroché à gauche : il raccourcit en se retirant vers elle. */
const remplissage: CSSProperties = {
  position: "absolute",
  left: 0,
  top: 0,
  bottom: 0,
  background: COULEURS.sauge,
};

const ligneHaut: CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  justifyContent: "center",
  gap: 12,
};

const surtitre: CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: ".18em",
  textTransform: "uppercase",
  color: "#EDE4D5",
};

const nom: CSSProperties = {
  margin: 0,
  maxWidth: 720,
  fontFamily: POLICE_TITRE_SPLASH,
  fontWeight: 400,
  lineHeight: 1.05,
  textWrap: "balance",
  textShadow: "0 2px 20px rgba(10,18,15,.5)",
};
